import React, { useState, useEffect } from 'react';
import { Award, Plus, Edit2, Trash2, X } from 'lucide-react';
import profileExtendedService from '../../services/profileExtended.service';
import toast from 'react-hot-toast';
import Button from '@components/common/Button';

const SkillsSection = () => {
  const [skills, setSkills] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingSkill, setEditingSkill] = useState(null);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    skillName: '',
    proficiencyLevel: 'intermediate'
  });
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);

  useEffect(() => {
    fetchSkills();
  }, []);

  const fetchSkills = async () => {
    try {
      const response = await profileExtendedService.getSkills();
      // Handle both response.data.data and response.data structures
      const skillsData = response.data?.data || response.data || [];
      setSkills(Array.isArray(skillsData) ? skillsData : []);
    } catch (error) {
      console.error('Failed to fetch skills:', error);
      setSkills([]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation(); // Prevent event from bubbling to parent form
    setLoading(true);

    try {
      if (editingSkill) {
        await profileExtendedService.updateSkill(editingSkill.id, formData);
        toast.success('Skill updated successfully');
      } else {
        await profileExtendedService.addSkill(formData);
        toast.success('Skill added successfully');
      }
      
      fetchSkills();
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save skill');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (skill) => {
    setEditingSkill(skill);
    setFormData({
      skillName: skill.skillName || '',
      proficiencyLevel: skill.proficiencyLevel || 'intermediate'
    });
    setShowForm(true);
  };

  const handleDelete = async (skillId) => {
    if (!window.confirm('Are you sure you want to delete this skill?')) return;

    try {
      await profileExtendedService.deleteSkill(skillId);
      toast.success('Skill deleted successfully');
      fetchSkills();
    } catch (error) {
      toast.error('Failed to delete skill');
    }
  };

  const resetForm = () => {
    setFormData({ skillName: '', proficiencyLevel: 'intermediate' });
    setEditingSkill(null);
    setShowForm(false);
    setSuggestions([]);
    setShowSuggestions(false);
  };

  const fetchSuggestions = async (searchTerm) => {
    if (!searchTerm || searchTerm.length < 2) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    setLoadingSuggestions(true);
    try {
      const response = await profileExtendedService.getSkillSuggestions(searchTerm, 10);
      const suggestionsData = response.data?.data || response.data || [];
      setSuggestions(Array.isArray(suggestionsData) ? suggestionsData : []);
      setShowSuggestions(true);
    } catch (error) {
      console.error('Failed to fetch skill suggestions:', error);
      setSuggestions([]);
    } finally {
      setLoadingSuggestions(false);
    }
  };

  const handleSkillNameChange = (e) => {
    const value = e.target.value;
    setFormData({ ...formData, skillName: value });
    fetchSuggestions(value);
  };

  const handleSuggestionClick = (suggestion) => {
    setFormData({ ...formData, skillName: suggestion.name });
    setShowSuggestions(false);
    setSuggestions([]);
  };

  const getProficiencyColor = (level) => {
    const colors = {
      beginner: 'bg-yellow-100 text-yellow-700',
      intermediate: 'bg-secondary-100 text-secondary-700',
      advanced: 'bg-purple-100 text-purple-700',
      expert: 'bg-green-100 text-green-700'
    };
    return colors[level] || colors.intermediate;
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3">
      <div className="flex justify-between items-center mb-2.5">
        <h3 className="text-sm font-semibold text-primary-800 flex items-center gap-2">
          <div className="w-6 h-6 rounded-md bg-primary-100 flex items-center justify-center flex-shrink-0">
            <Award className="w-3.5 h-3.5 text-primary-700" />
          </div>
          Skills
        </h3>
        {!showForm && (
          <Button
            onClick={() => setShowForm(true)}
            variant="primary"
            size="sm"
          >
            <Plus className="w-3.5 h-3.5 mr-1" />
            Add Skill
          </Button>
        )}
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} className="mb-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
          <div className="flex justify-between items-center mb-5">
            <h4 className="font-semibold text-primary-800">
              {editingSkill ? 'Edit Skill' : 'Add New Skill'}
            </h4>
            <Button
              type="button"
              onClick={resetForm}
              variant="ghost"
              className="p-1"
            >
              <X className="w-5 h-5" />
            </Button>
          </div>

          <div className="space-y-4">
            <div className="relative">
              <label className="block text-sm font-medium text-primary-800 mb-2">
                Skill Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.skillName}
                onChange={handleSkillNameChange}
                onFocus={() => formData.skillName.length >= 2 && suggestions.length > 0 && setShowSuggestions(true)}
                onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                className="w-full px-4 py-2.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="e.g., JavaScript, Project Management"
                required
                autoComplete="off"
              />
              {showSuggestions && suggestions.length > 0 && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                  {suggestions.map((suggestion) => (
                    <div
                      key={suggestion.id}
                      onClick={() => handleSuggestionClick(suggestion)}
                      className="px-4 py-2.5 hover:bg-primary-50 cursor-pointer border-b border-gray-100 last:border-b-0 transition-colors"
                    >
                      <span className="text-sm font-medium text-primary-900">{suggestion.name}</span>
                    </div>
                  ))}
                </div>
              )}
              {loadingSuggestions && (
                <div className="absolute right-3 top-9 text-gray-400">
                  <div className="animate-spin h-4 w-4 border-2 border-primary-500 border-t-transparent rounded-full"></div>
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-primary-800 mb-2">
                Proficiency Level
              </label>
              <select
                value={formData.proficiencyLevel}
                onChange={(e) => setFormData({ ...formData, proficiencyLevel: e.target.value })}
                className="w-full px-4 py-2.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white"
              >
                <option value="beginner">Beginner</option>
                <option value="intermediate">Intermediate</option>
                <option value="advanced">Advanced</option>
                <option value="expert">Expert</option>
              </select>
            </div>
          </div>

          <div className="flex flex-wrap gap-3 mt-5">
            <Button
              type="submit"
              disabled={loading}
              variant="primary"
              loading={loading}
            >
              {editingSkill ? 'Update' : 'Add'}
            </Button>
            <Button
              type="button"
              onClick={resetForm}
              variant="outline"
            >
              Cancel
            </Button>
          </div>
        </form>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 max-h-64 overflow-y-auto pr-1">
        {skills.length === 0 ? (
          <p className="text-gray-500 text-center py-8 text-xs col-span-full">No skills added yet. Click "Add Skill" to get started.</p>
        ) : (
          skills.map((skill) => (
            <div
              key={skill.id}
              className="group relative p-2 border border-gray-200 rounded-md hover:border-secondary-300 hover:shadow-sm transition-all bg-white"
            >
              <div className="flex flex-col gap-1 pr-8">
                <p className="text-xs font-semibold text-primary-900 line-clamp-1 break-words">{skill.skillName}</p>
                <span className={`inline-block px-1.5 py-0.5 text-[10px] font-medium rounded self-start ${getProficiencyColor(skill.proficiencyLevel)}`}>
                  {skill.proficiencyLevel}
                </span>
              </div>
              <div className="absolute top-1.5 right-1.5 flex gap-0.5 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
                <Button
                  onClick={() => handleEdit(skill)}
                  variant="ghost"
                  className="p-0.5 text-blue-600 hover:bg-blue-50 rounded"
                >
                  <Edit2 className="w-3 h-3" />
                </Button>
                <Button
                  onClick={() => handleDelete(skill.id)}
                  variant="ghost"
                  className="p-0.5 text-red-600 hover:bg-red-50 rounded"
                >
                  <Trash2 className="w-3 h-3" />
                </Button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default SkillsSection;